import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { NonAdminPreviewBanner } from '../../components/NonAdminPreviewBanner';
import { useAuthContext } from '../../context/AuthContext';
import { useNotificationContext } from '../../context/NotificationContext';
import { ApiService } from '../../services/api';
import {
  ClassificationType,
  CropEmbeddingSearchResponse,
  EmbeddingSearchParams,
  EmbeddingSelectionMode,
  ProductBarcodeResponse,
  ProductImageSource,
  ProductVariantResponse,
  Store,
} from '../../types';
import { EmbeddingTile } from './components/EmbeddingTile';
import { EmbeddingTypeFilter } from './components/EmbeddingTypeFilter';
import { ImageZoomModal } from './components/ImageZoomModal';
import { PendingEmbeddingsDialog } from './components/PendingEmbeddingsDialog';
import { PendingImage, ProductImagesModal } from './components/ProductImagesModal';
import { ProductImageThumbnails } from './components/ProductImageThumbnails';
import { UseAsProductImageDialog } from './components/UseAsProductImageDialog';
import { useProductImages } from './hooks/useProductImages';

function scrollToEmbedding(cropId: string) {
  const el = document.getElementById(`embedding-${cropId}`);

  if (!el) {
    return;
  }

  const scroll = () => {
    setTimeout(() => {
      document
        .getElementById(`embedding-${cropId}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 1000);
  };

  const img = el.querySelector('img');

  if (img) {
    if (img.complete) {
      scroll();
    } else {
      img.addEventListener('load', scroll, { once: true });
      img.addEventListener('error', scroll, { once: true });
    }
  }
}

const EmbeddingDetailPage: React.FC = () => {
  const { logout, token, hasInternalAccess } = useAuthContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showInfoToast } = useNotificationContext();

  // Filters - initialize from query params
  const [chainId, setChainId] = useState<number | ''>(() => {
    const param = searchParams.get('chain_id');
    return param ? Number(param) : '';
  });
  const [storeId, setStoreId] = useState<string>(() => {
    return searchParams.get('store_id') || '';
  });
  const [searchBy, setSearchBy] = useState<'upc' | 'itemNumber' | 'barcode'>(() => {
    if (searchParams.get('item_number')) return 'itemNumber';
    if (searchParams.get('barcode')) return 'barcode';
    return 'upc';
  });
  const [upc, setUpc] = useState<string>(() => {
    return searchParams.get('upc') || '';
  });
  const [itemNumber, setItemNumber] = useState<string>(() => {
    return searchParams.get('item_number') || '';
  });
  const [barcode, setBarcode] = useState<string>(() => {
    return searchParams.get('barcode') || '';
  });
  const [extendedSearch, setExtendedSearch] = useState<boolean>(() => {
    return searchParams.get('extended_search') === 'true';
  });

  // Data
  const [embeddings, setEmbeddings] = useState<CropEmbeddingSearchResponse[]>([]);
  // Initialize loading based on initial params so dependent effects skip
  // their first-pass fetch until embeddings settle (avoids duplicate
  // variants/product fetches when store_id is not in params).
  const [loading, setLoading] = useState(() => {
    const chainIdParam = searchParams.get('chain_id');
    const hasQuery =
      !!searchParams.get('upc') ||
      !!searchParams.get('item_number') ||
      !!searchParams.get('barcode');
    return !!chainIdParam && hasQuery;
  });
  const [error, setError] = useState<string | null>(null);

  const [filterType, setFilterType] = useState<ClassificationType | 'all'>('all');

  // Selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [rejectedIds, setRejectedIds] = useState<Set<string>>(new Set());
  const initialCropIdHandled = useRef(false);
  const initialCropId = useRef(searchParams.get('crop_id'));
  const [selectingMode, setSelectingMode] = useState<EmbeddingSelectionMode | null>(null);

  // Save state
  const [saving, setSaving] = useState(false);
  const [showPendingDialog, setShowPendingDialog] = useState(false);

  // Refresh trigger
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const activeChainId = searchParams.get('chain_id');
  const activeUpc = searchParams.get('upc');
  const activeItemNumber = searchParams.get('item_number');
  const activeBarcode = searchParams.get('barcode');

  // The product lookup endpoint accepts UPC, item number, and barcode
  const effectiveUpc = activeUpc || activeItemNumber || activeBarcode || '';

  // Stores + chain names map for cross-chain image labels
  const [stores, setStores] = useState<Store[]>([]);
  const [chainNamesMap, setChainNamesMap] = useState<Record<number, string>>({});
  useEffect(() => {
    if (!token || !hasInternalAccess) return;
    ApiService.fetchStores(token).then((fetchedStores: Store[]) => {
      setStores(fetchedStores);
      const map: Record<number, string> = {};
      for (const store of fetchedStores) {
        if (!map[store.chainId]) {
          map[store.chainId] = store.chainLegibleName;
        }
      }
      setChainNamesMap(map);
    });
  }, [token, hasInternalAccess]);

  // Resolve the store for the current search (used for product lookup + primary image upload)
  const activeStore = useMemo(() => {
    if (!activeChainId) return undefined;
    const chainIdNum = Number(activeChainId);
    const storeIdParam = searchParams.get('store_id');
    if (storeIdParam) {
      return stores.find((s) => s.storeId === Number(storeIdParam));
    }

    // Without an explicit store_id, wait for embeddings to finish loading
    // before resolving a fallback — otherwise dependent fetches would fire
    // twice (once with the fallback, again once embeddings arrive).
    if (loading) return undefined;

    // Try to find a store from embeddings data first
    const embeddingStoreId = embeddings.length > 0 ? embeddings[0].storeId : undefined;
    if (embeddingStoreId) {
      const storeFromEmbedding = stores.find((s) => s.storeId === embeddingStoreId);
      if (storeFromEmbedding) return storeFromEmbedding;
    }

    if (chainIdNum === 46) {
      // FIXME: Use Lidl Fulwell (1861) until v2 metadata is fully propagated to all stores
      return stores.find((s) => s.storeId === 1276);
    }

    // Fallback to chain-level match
    return stores.find((s) => s.chainId === chainIdNum && s.isActive);
  }, [activeChainId, searchParams, stores, embeddings, loading]);

  // Always fetch product data (needed for product_id in primary image upload + fallback display)
  const [productData, setProductData] = useState<ProductBarcodeResponse | null>(null);
  const [productDataLoading, setProductDataLoading] = useState(false);
  const activeStoreName = activeStore?.storeName || searchParams.get('store_name') || '';
  const refetchProductData = useCallback(() => {
    if (!token || !effectiveUpc || !activeStoreName) {
      setProductData(null);
      setProductDataLoading(false);
      return;
    }

    const codeType = activeItemNumber ? 'item_number' : activeUpc ? 'upc' : undefined;

    setProductDataLoading(true);
    ApiService.getStoreProductByBarcode(token, activeStoreName, effectiveUpc, codeType)
      .then(setProductData)
      .catch(() => setProductData(null))
      .finally(() => setProductDataLoading(false));
  }, [token, effectiveUpc, activeStoreName, activeItemNumber, activeUpc]);

  useEffect(() => {
    refetchProductData();
  }, [refetchProductData]);

  // Real UPC derived from returned data — used for sidebar APIs that require an actual UPC
  // (fetchVariants, useProductImages). Falls back to activeUpc when no data is available yet.
  const derivedUpc = useMemo(() => {
    if (!productData) {
      return;
    }

    // When the user queries by item number, normalized_barcode is just the
    // item number zero-padded — not a real UPC. The variants/images endpoints
    // need the actual UPC, which the API returns separately as normalized_upc.
    const queriedByItemNumber = !!activeItemNumber;
    const queryMatchesItemNumber =
      !!productData.item_number &&
      (activeUpc === productData.item_number || activeBarcode === productData.item_number);

    if (queriedByItemNumber || queryMatchesItemNumber) {
      return productData.normalized_upc;
    }

    if (productData.normalized_barcode) {
      return productData.normalized_barcode;
    }

    return activeUpc || '';
  }, [productData, activeUpc, activeItemNumber, activeBarcode]);

  // Variants
  const [variants, setVariants] = useState<ProductVariantResponse[]>([]);
  const [variantsExpanded, setVariantsExpanded] = useState(true);
  useEffect(() => {
    if (!token || !activeStoreName || !derivedUpc) {
      setVariants([]);
      return;
    }
    ApiService.fetchVariants(token, activeStoreName, derivedUpc)
      .then((vs) => setVariants(vs.filter((v) => !v.isCaseUpc || v.frontImage || v.backImage)))
      .catch(() => setVariants([]));
  }, [token, activeStoreName, derivedUpc]);

  // Product images — wait until embeddings finish loading so we don't fetch
  // twice (once without store_name, again once embeddings resolve the store).
  const productImages = useProductImages({
    chainId: !loading && activeChainId ? Number(activeChainId) : '',
    upc: derivedUpc || '',
    storeName: activeStoreName || undefined,
  });

  // Image management modals
  const [showImagesModal, setShowImagesModal] = useState(false);
  const [zoomImageSrc, setZoomImageSrc] = useState<string | null>(null);
  const [useAsProductImageEmbedding, setUseAsProductImageEmbedding] =
    useState<CropEmbeddingSearchResponse | null>(null);
  const [imagesModalPendingNew, setImagesModalPendingNew] = useState<
    { image: PendingImage; targetType: 'primary' | 'alternative' } | undefined
  >(undefined);

  useEffect(() => {
    if (selectingMode === EmbeddingSelectionMode.ACTIVATING) {
      showInfoToast('Select all embeddings to activate');
    } else if (selectingMode === EmbeddingSelectionMode.DEACTIVATING) {
      showInfoToast('Select all embeddings to deactivate');
    }
  }, [selectingMode, showInfoToast]);

  // Handle checkbox toggle
  const handleCheckboxChange = useCallback(
    ({ productEmbeddingCropId: embeddingId, isActive, isPending }: CropEmbeddingSearchResponse) => {
      if (!embeddingId) {
        return;
      }

      if (rejectedIds.has(embeddingId)) {
        setRejectedIds((prev) => {
          const newSet = new Set(prev);

          newSet.delete(embeddingId);

          if (newSet.size === 0 && selectedIds.size === 0) {
            setSelectingMode(null);
          }

          return newSet;
        });

        return;
      }

      if (isPending && selectingMode === EmbeddingSelectionMode.DEACTIVATING) {
        setRejectedIds((prev) => {
          const newSet = new Set(prev);
          newSet.add(embeddingId);

          return newSet;
        });
        return;
      }

      setSelectedIds((prev) => {
        const newSet = new Set(prev);

        if (newSet.has(embeddingId)) {
          newSet.delete(embeddingId);

          if (newSet.size === 0 && rejectedIds.size === 0) {
            setSelectingMode(null);
          }

          return newSet;
        }

        // If this is the first selection, store its active state
        if (prev.size === 0 && rejectedIds.size === 0) {
          setSelectingMode(
            isActive ? EmbeddingSelectionMode.DEACTIVATING : EmbeddingSelectionMode.ACTIVATING
          );
          newSet.add(embeddingId);
          return newSet;
        }

        if (rejectedIds.size > 0) {
          // When rejecting, can only select active items (for deactivation)
          if (isActive) {
            newSet.add(embeddingId);
          }

          return newSet;
        }

        // Only allow selection if the item matches the first selected state
        const expectedMode = isActive
          ? EmbeddingSelectionMode.DEACTIVATING
          : EmbeddingSelectionMode.ACTIVATING;

        if (selectingMode === expectedMode) {
          newSet.add(embeddingId);
        }

        return newSet;
      });
    },
    [rejectedIds, selectedIds, selectingMode]
  );

  // Fetch embeddings when query params change
  useEffect(() => {
    const chainIdParam = searchParams.get('chain_id');
    const upcParam = searchParams.get('upc');

    // Reset selection state when search params change
    setSelectedIds(new Set());
    setRejectedIds(new Set());
    setSelectingMode(null);

    const itemNumberParam = searchParams.get('item_number');
    const barcodeParam = searchParams.get('barcode');
    const storeIdParam = searchParams.get('store_id');

    // Only fetch if required params are present
    if (!token || !chainIdParam || (!upcParam && !itemNumberParam && !barcodeParam)) {
      setEmbeddings([]);
      return;
    }

    // Set loading synchronously so dependent effects (activeStore) can skip
    // fetches until embeddings settle.
    setLoading(true);

    const fetchEmbeddings = async () => {
      setError(null);

      try {
        const extendedSearchParam = searchParams.get('extended_search') === 'true';

        const params: EmbeddingSearchParams = {
          chainId: Number(chainIdParam),
          ...(storeIdParam && { storeId: Number(storeIdParam) }),
          ...(upcParam && { normalizedBarcodes: upcParam }),
          ...(itemNumberParam
            ? { itemNumber: itemNumberParam }
            : barcodeParam
              ? { barcode: barcodeParam }
              : { extendedSearch: extendedSearchParam }),
          limit: 500,
        };

        const embeddings = await ApiService.searchEmbeddings(token, params);

        setEmbeddings(embeddings);
      } catch (err) {
        console.error('Error fetching embeddings:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch embeddings');
        setEmbeddings([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEmbeddings();
  }, [token, searchParams, refreshTrigger]);

  // Handle initial crop_id selection once after first successful fetch
  useEffect(() => {
    const cropId = initialCropId.current;
    if (!cropId || initialCropIdHandled.current || embeddings.length === 0) {
      return;
    }
    const match = embeddings.find((e) => e.productEmbeddingCropId === cropId);
    if (match) {
      initialCropIdHandled.current = true;
      handleCheckboxChange(match);
      requestAnimationFrame(() => scrollToEmbedding(cropId));
    }
  }, [embeddings, handleCheckboxChange]);

  // Handle search button click - update URL query params
  const handleSearch = () => {
    const params: Record<string, string> = {};

    if (chainId) {
      params.chain_id = String(chainId);
    }
    if (storeId) {
      params.store_id = storeId;
    }
    const storeNameParam = searchParams.get('store_name');
    if (storeNameParam) {
      params.store_name = storeNameParam;
    }
    if (searchBy === 'upc') {
      if (upc) {
        params.upc = upc;
      }
      if (extendedSearch) {
        params.extended_search = 'true';
      }
    } else if (searchBy === 'itemNumber') {
      if (itemNumber) {
        params.item_number = itemNumber;
      }
    } else if (searchBy === 'barcode') {
      if (barcode) {
        params.barcode = barcode;
      }
    }

    setSearchParams(params);
  };

  // Handle Enter key press in input fields
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (
      e.key === 'Enter' &&
      chainId &&
      (searchBy === 'upc' ? upc : searchBy === 'itemNumber' ? itemNumber : barcode)
    ) {
      handleSearch();
    }
  };

  const handleReject = (embeddingId: string) => {
    if (selectingMode === EmbeddingSelectionMode.ACTIVATING) {
      return;
    }

    setRejectedIds((prev) => {
      const newSet = new Set(prev);
      newSet.add(embeddingId);

      // When starting rejection with no other selections, set to deactivate mode
      if (selectedIds.size === 0 && selectingMode === null) {
        setSelectingMode(EmbeddingSelectionMode.DEACTIVATING);
      }

      return newSet;
    });
  };

  // Filter embeddings based on classification type
  const filteredEmbeddings = useMemo(() => {
    if (filterType === 'all') {
      return embeddings;
    }
    return embeddings.filter((embedding) => embedding.classificationType === filterType);
  }, [embeddings, filterType]);

  const pendingEmbeddings = useMemo(
    () => filteredEmbeddings.filter(({ isPending }) => isPending),
    [filteredEmbeddings]
  );

  const notPendingEmbeddings = useMemo(
    () =>
      filteredEmbeddings
        .filter(({ isPending }) => !isPending)
        .sort((a, b) => {
          if (a.isActive !== b.isActive) {
            return a.isActive ? -1 : 1;
          }

          const aIsScan = a.source === 'scan';
          const bIsScan = b.source === 'scan';
          if (aIsScan !== bIsScan) {
            return aIsScan ? -1 : 1;
          }

          const aIsCarton = a.classificationType === 'carton';
          const bIsCarton = b.classificationType === 'carton';
          if (aIsCarton !== bIsCarton) {
            return aIsCarton ? -1 : 1;
          }

          return 0;
        }),
    [filteredEmbeddings]
  );

  const activeEmbeddings = useMemo(
    () => notPendingEmbeddings.filter(({ isActive }) => isActive),
    [notPendingEmbeddings]
  );

  const inactiveEmbeddings = useMemo(
    () => notPendingEmbeddings.filter(({ isActive }) => !isActive),
    [notPendingEmbeddings]
  );

  // Get pending embedding IDs not in the current selection or rejection
  const getRemainingPendingIds = useCallback(() => {
    const selectedOrRejected = new Set([...selectedIds, ...rejectedIds]);
    return pendingEmbeddings
      .filter((e) => e.productEmbeddingCropId && !selectedOrRejected.has(e.productEmbeddingCropId))
      .map((e) => e.productEmbeddingCropId!);
  }, [pendingEmbeddings, selectedIds, rejectedIds]);

  // Core save logic
  const executeSave = useCallback(
    async (includeRemainingPending: boolean) => {
      if (!token || selectingMode === null) return;

      setSaving(true);
      setError(null);

      try {
        const selectedEmbeddingIds = [...selectedIds, ...rejectedIds];
        const newActiveState = selectingMode === EmbeddingSelectionMode.ACTIVATING;

        // Save selected embeddings with the chosen active state
        if (selectedEmbeddingIds.length > 0) {
          await ApiService.updateEmbeddingsActiveState(token, selectedEmbeddingIds, newActiveState);
        }

        // Save remaining pending embeddings with the OPPOSITE active state
        if (includeRemainingPending) {
          const remainingIds = getRemainingPendingIds();
          if (remainingIds.length > 0) {
            await ApiService.updateEmbeddingsActiveState(token, remainingIds, !newActiveState);
          }
        }

        setSelectedIds(new Set());
        setRejectedIds(new Set());
        setSelectingMode(null);
        setRefreshTrigger((prev) => prev + 1);
      } catch (err) {
        console.error('Error saving embeddings:', err);
        setError(err instanceof Error ? err.message : 'Failed to save changes');
      } finally {
        setSaving(false);
      }
    },
    [token, selectingMode, selectedIds, rejectedIds, getRemainingPendingIds]
  );

  // Handle save button click - check for remaining pending embeddings
  const handleSave = () => {
    if (!token || selectingMode === null || (selectedIds.size === 0 && rejectedIds.size === 0)) {
      return;
    }

    const remainingPendingIds = getRemainingPendingIds();
    const pendingIdSet = new Set(pendingEmbeddings.map((e) => e.productEmbeddingCropId));
    const hasSelectedPending = [...selectedIds, ...rejectedIds].some((id) => pendingIdSet.has(id));

    if (remainingPendingIds.length > 0 && hasSelectedPending) {
      setShowPendingDialog(true);
    } else {
      executeSave(false);
    }
  };

  const handleFilterChange = (value: ClassificationType | 'all') => {
    setFilterType(value);
  };

  // Handle "Use as Product Image" from embedding tile
  const handleUseAsProductImage = useCallback((embedding: CropEmbeddingSearchResponse) => {
    setUseAsProductImageEmbedding(embedding);
  }, []);

  const handleUseAsProductImageConfirm = useCallback(
    (targetType: 'primary' | 'alternative', imageBlob: Blob) => {
      const embedding = useAsProductImageEmbedding;
      setUseAsProductImageEmbedding(null);
      setImagesModalPendingNew({
        image: {
          imageUrl: embedding?.vizUrl ?? '',
          isNew: true,
          blob: imageBlob,
          source: ProductImageSource.Embedding,
          origin: 'control',
        },
        targetType,
      });
      setShowImagesModal(true);
    },
    [useAsProductImageEmbedding]
  );

  // Handle save from ProductImagesModal
  const handleProductImagesSave = useCallback(
    async (changes: {
      primary: {
        imageUrl: string;
        isNew: boolean;
        productImageId?: number;
        blob?: Blob;
        source: string;
        origin?: string;
      } | null;
      alternatives: {
        imageUrl: string;
        isNew: boolean;
        productImageId?: number;
        blob?: Blob;
        source: string;
        origin?: string;
      }[];
    }) => {
      const primaryPosition = changes.primary?.productImageId
        ? (productImages.images.find(
            (img) => img.productImageId === changes.primary!.productImageId
          )?.position ?? 0)
        : 0;

      const allPending = [
        ...(changes.primary ? [{ ...changes.primary, position: primaryPosition }] : []),
        ...changes.alternatives.map((img, i) => ({ ...img, position: primaryPosition + i + 1 })),
      ];

      // Find existing images that were removed
      const keepIds = new Set(
        allPending.filter((img) => img.productImageId).map((img) => img.productImageId!)
      );
      const toDelete = productImages.images.filter(
        (img) => img.productImageId && !keepIds.has(img.productImageId)
      );

      // Upload new images first (so we don't lose existing images if upload fails)
      for (const img of allPending) {
        if (img.isNew && img.blob) {
          const result = await productImages.uploadImage(img.blob, img.position, img.source);
          if (!result) throw new Error('Failed to upload image');
        }
      }

      // Delete removed images (continue on failure so reorder still runs)
      for (const img of toDelete) {
        await productImages.deleteImage(img.productImageId!);
      }

      // Reorder: send position updates for any existing images whose position changed.
      const currentPositionMap = new Map(
        productImages.images.map((img) => [img.productImageId, img.position])
      );

      const reorderItems = allPending
        .filter((img) => !img.isNew && img.productImageId)
        .filter((img) => currentPositionMap.get(img.productImageId!) !== img.position)
        .map((img) => ({ product_image_id: img.productImageId!, position: img.position }));

      if (reorderItems.length > 0) {
        await productImages.reorderImages(reorderItems);
      }

      await productImages.refetch();
    },
    [productImages]
  );

  // Get product details from first embedding row, or fall back to barcode API
  const productDetails = useMemo(() => {
    if (productData?.item_number || productData?.product_name) {
      // When the query resolved to an item number, normalized_barcode is the
      // zero-padded item number — prefer showing the real UPC if available.
      const queriedByItemNumber = !!activeItemNumber;
      const queryMatchesItemNumber =
        !!productData.item_number &&
        (activeUpc === productData.item_number || activeBarcode === productData.item_number);
      const showUpcInstead =
        (queriedByItemNumber || queryMatchesItemNumber) && !!productData.normalized_upc;

      return {
        productName: productData.product_name,
        itemNumber: productData.item_number,
        normalizedBarcode: showUpcInstead
          ? productData.normalized_upc!
          : productData.normalized_barcode,
        normalizedBarcodeIsUpc: showUpcInstead,
        barcode: productData.barcode,
      };
    }

    if (embeddings.length > 0 && !productDataLoading) {
      const e = embeddings[0];
      return {
        productName: e.productName,
        itemNumber: e.itemNumber,
        normalizedBarcode: e.normalizedBarcode,
        normalizedBarcodeIsUpc: false,
        barcode: e.barcode,
      };
    }
    return null;
  }, [embeddings, productData, productDataLoading, activeItemNumber, activeUpc, activeBarcode]);

  const isDerivedProduct =
    !!productDetails && !(productData?.item_number || productData?.product_name);

  return (
    <div
      className={`min-h-screen bg-gray-50 px-6 pt-2 pb-6 ${selectedIds.size > 0 || rejectedIds.size > 0 ? 'pb-24' : ''}`}
    >
      <div className="max-w-7xl mx-auto">
        <NonAdminPreviewBanner />
        <div className="flex items-start justify-between">
          <h1 className="text-3xl font-bold text-gray-900 mb-3">Embedding Details</h1>
          <div className="flex items-center gap-2">
            {hasInternalAccess && searchParams.get('chain_id') && (
              <Link
                to={`/embeddings?chain_id=${searchParams.get('chain_id')}${searchParams.get('store_id') ? `&store_id=${searchParams.get('store_id')}` : ''}${searchParams.get('upc') ? `&upc=${searchParams.get('upc')}` : ''}`}
                className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-md"
              >
                Embedding List
              </Link>
            )}
            <button
              onClick={() => logout()}
              className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-md"
            >
              Sign Out
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Chain ID <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={chainId}
                onChange={(e) => setChainId(e.target.value ? Number(e.target.value) : '')}
                onKeyDown={handleKeyDown}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter chain ID"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Store ID{' '}
                <span className="text-xs font-normal text-gray-400">
                  (product lookup only, embeddings are per chain)
                </span>
              </label>
              <input
                type="number"
                value={storeId}
                onChange={(e) => setStoreId(e.target.value)}
                onKeyDown={handleKeyDown}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter store ID (optional)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search by <span className="text-red-500">*</span>
              </label>
              <select
                value={searchBy}
                onChange={(e) => setSearchBy(e.target.value as 'upc' | 'itemNumber' | 'barcode')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="upc">UPC</option>
                <option value="itemNumber">Item Number</option>
                <option value="barcode">Barcode</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            {searchBy === 'upc' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    UPC <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={upc}
                    onChange={(e) => setUpc(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter UPC"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Include all variants
                  </label>
                  <select
                    value={extendedSearch ? 'true' : 'false'}
                    onChange={(e) => setExtendedSearch(e.target.value === 'true')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                  </select>
                </div>
              </>
            )}
            {searchBy === 'itemNumber' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Item Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={itemNumber}
                  onChange={(e) => setItemNumber(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter item number"
                />
              </div>
            )}
            {searchBy === 'barcode' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Barcode <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={barcode}
                  onChange={(e) => setBarcode(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter barcode"
                />
              </div>
            )}
          </div>

          <button
            onClick={handleSearch}
            disabled={
              !chainId ||
              !(searchBy === 'upc' ? upc : searchBy === 'itemNumber' ? itemNumber : barcode) ||
              loading
            }
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {loading ? 'Loading...' : 'Search'}
          </button>
        </div>

        {/* Error message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Empty states */}
        {!loading && !activeChainId && !activeUpc && !activeItemNumber && !activeBarcode && (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <p className="text-gray-500 text-lg">
              Please enter a Chain ID and UPC, Item Number, or Barcode to view embeddings
            </p>
          </div>
        )}

        {/* Main content - Two column layout */}
        {!loading && activeChainId && (activeUpc || activeItemNumber || activeBarcode) && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left column - Product details */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow px-6 py-3 sticky top-2 max-h-[calc(100vh-1rem)] overflow-y-auto">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Product Details</h2>

                {productDataLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-green-600" />
                  </div>
                ) : (
                  <>
                {/* Product image */}
                {(() => {
                  const managedPrimary = productImages.primaryImage?.productImageId
                    ? productImages.primaryImage
                    : null;
                  const shelfImage =
                    productImages.allImages.find((img) => !img.productImageId) ?? null;
                  const displayImage = managedPrimary ?? shelfImage;

                  const imageSrc = displayImage?.imageUri;

                  if (!imageSrc) {
                    return null;
                  }

                  const isShelf = !displayImage.productImageId;
                  return (
                    <div className="mb-4 relative group">
                      {isShelf && (
                        <span className="absolute top-2 left-2 z-10 px-2 py-0.5 text-xs font-medium rounded bg-gray-800 bg-opacity-70 text-white">
                          Shelf Image
                        </span>
                      )}
                      <img
                        src={imageSrc}
                        alt={isShelf ? 'Shelf' : 'Product'}
                        className={`w-auto max-h-[300px] rounded-lg border border-gray-200 ${isShelf ? 'opacity-75' : ''}`}
                      />
                      {!isDerivedProduct && (
                        <button
                          onClick={() => setZoomImageSrc(imageSrc)}
                          className="absolute top-2 right-2 p-1.5 bg-white bg-opacity-75 rounded-md shadow hover:bg-opacity-100 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <ZoomOutMapIcon sx={{ fontSize: 18 }} />
                        </button>
                      )}
                    </div>
                  );
                })()}

                {/* Alternative images + Manage button */}
                <div className="mb-4">
                  <ProductImageThumbnails
                    alternativeImages={productImages.alternativeImages.filter(
                      (img) => img.productImageId
                    )}
                    shelfImages={[]}
                    otherChainImages={productImages.allImages.filter(
                      (img) => img.productImageId && img.chainId !== Number(activeChainId)
                    )}
                    currentChainId={activeChainId ? Number(activeChainId) : 0}
                    chainNamesMap={chainNamesMap}
                    onManageClick={() => setShowImagesModal(true)}
                    onImageClick={(url) => setZoomImageSrc(url)}
                    manageDisabled={isDerivedProduct}
                    manageDisabledTitle="Product not found"
                  />
                </div>

                {/* Product info */}
                {productDetails && (
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-gray-500">
                        {isDerivedProduct ? 'Derived Product Name' : 'Product Name'}
                      </p>
                      <p className="text-base font-medium text-gray-900">
                        {productDetails.productName || 'N/A'}
                      </p>
                    </div>

                    {productDetails.itemNumber && (
                      <div>
                        <p className="text-sm text-gray-500">Item number</p>
                        <p className="text-base font-medium text-gray-900">
                          {productDetails.itemNumber}
                        </p>
                      </div>
                    )}

                    <div>
                      <p className="text-sm text-gray-500">
                        {productDetails.normalizedBarcodeIsUpc
                          ? 'Primary UPC'
                          : 'Normalized Barcode'}
                      </p>
                      <p className="text-base font-medium text-gray-900">
                        {productDetails.normalizedBarcode}
                      </p>
                    </div>

                    <div>
                      <p className="text-sm text-gray-500">Barcode</p>
                      <p className="text-base font-medium text-gray-900">
                        {productDetails.barcode}
                      </p>
                    </div>
                    <a
                      href={`https://www.google.com/search?q=${productDetails.barcode}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 mt-1"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                      Search on Google
                    </a>
                  </div>
                )}

                {/* Variants */}
                {variants.length > 0 && (
                  <div className="mt-4 border-t border-gray-200 pt-4">
                    <button
                      onClick={() => setVariantsExpanded((prev) => !prev)}
                      className="flex items-center justify-between w-full text-left"
                    >
                      <h3 className="text-sm font-semibold text-gray-900">
                        Variants ({variants.length})
                      </h3>
                      {variantsExpanded ? (
                        <ExpandLessIcon className="text-gray-600" sx={{ fontSize: 20 }} />
                      ) : (
                        <ExpandMoreIcon className="text-gray-600" sx={{ fontSize: 20 }} />
                      )}
                    </button>
                    {variantsExpanded && (
                      <div className="mt-3 space-y-3">
                        {[...variants]
                          .sort((a, b) => {
                            const aImg = a.frontImage || a.backImage ? 0 : 1;
                            const bImg = b.frontImage || b.backImage ? 0 : 1;
                            if (aImg !== bImg) return aImg - bImg;
                            const aCase = a.isCaseUpc ? 1 : 0;
                            const bCase = b.isCaseUpc ? 1 : 0;
                            return aCase - bCase;
                          })
                          .map((variant) => (
                            <div
                              key={variant.upc}
                              className="p-2 rounded-lg border border-gray-100 bg-gray-50"
                            >
                              <p className="text-sm font-medium text-gray-900 truncate mb-2">
                                {variant.name || 'Unknown'}
                                {variant.isCaseUpc && variant.casePack != null && (
                                  <span className="text-gray-500"> ({variant.casePack})</span>
                                )}
                              </p>
                              <div className="flex items-center gap-3">
                                {variant.frontImage || variant.backImage ? (
                                  <div className="flex gap-1 flex-shrink-0">
                                    {variant.frontImage ? (
                                      <div className="relative group">
                                        <img
                                          src={variant.frontImage}
                                          alt={`${variant.name || variant.upc} front`}
                                          className="w-[100px] h-[100px] object-contain rounded cursor-pointer border border-gray-200"
                                          onClick={() => setZoomImageSrc(variant.frontImage!)}
                                        />
                                        <button
                                          onClick={() => setZoomImageSrc(variant.frontImage!)}
                                          className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-30 rounded transition-all"
                                        >
                                          <ZoomOutMapIcon
                                            className="text-white opacity-0 group-hover:opacity-100 transition-opacity"
                                            sx={{ fontSize: 16 }}
                                          />
                                        </button>
                                        <span className="absolute bottom-0.5 left-0.5 text-[9px] bg-black bg-opacity-50 text-white px-1 rounded">
                                          Front
                                        </span>
                                      </div>
                                    ) : (
                                      <div className="w-[100px] h-[100px] rounded border border-gray-200 bg-gray-100 flex items-center justify-center">
                                        <span className="text-gray-400 text-xs">No front</span>
                                      </div>
                                    )}
                                    {variant.backImage ? (
                                      <div className="relative group">
                                        <img
                                          src={variant.backImage}
                                          alt={`${variant.name || variant.upc} back`}
                                          className="w-[100px] h-[100px] object-contain rounded cursor-pointer border border-gray-200"
                                          onClick={() => setZoomImageSrc(variant.backImage!)}
                                        />
                                        <button
                                          onClick={() => setZoomImageSrc(variant.backImage!)}
                                          className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-30 rounded transition-all"
                                        >
                                          <ZoomOutMapIcon
                                            className="text-white opacity-0 group-hover:opacity-100 transition-opacity"
                                            sx={{ fontSize: 16 }}
                                          />
                                        </button>
                                        <span className="absolute bottom-0.5 left-0.5 text-[9px] bg-black bg-opacity-50 text-white px-1 rounded">
                                          Back
                                        </span>
                                      </div>
                                    ) : (
                                      <div className="w-[100px] h-[100px] rounded border border-gray-200 bg-gray-100 flex items-center justify-center">
                                        <span className="text-gray-400 text-xs">No back</span>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <div className="flex gap-1 flex-shrink-0">
                                    <div className="w-[100px] h-[100px] rounded border border-gray-200 bg-gray-100 flex items-center justify-center">
                                      <span className="text-gray-400 text-xs">N/A</span>
                                    </div>
                                    <div className="w-[100px] h-[100px] rounded border border-gray-200 bg-gray-100 flex items-center justify-center">
                                      <span className="text-gray-400 text-xs">N/A</span>
                                    </div>
                                  </div>
                                )}
                                <div className="min-w-0 flex-1">
                                  <p className="text-xs text-gray-500">{variant.upc}</p>
                                  {variant.itemNumber && (
                                    <p className="text-xs text-gray-500">
                                      Item: {variant.itemNumber}
                                    </p>
                                  )}
                                  {variant.isCaseUpc && (
                                    <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-medium inline-block mt-0.5">
                                      Case Pack
                                    </span>
                                  )}
                                  {!variant.isActive && (
                                    <span className="text-xs px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-800 font-medium inline-block mt-0.5 ml-1">
                                      Inactive
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                )}
                  </>
                )}
              </div>
            </div>

            {/* Right column - Embeddings grid */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow px-6 py-3">
                {embeddings.length === 0 && (
                  <div className="p-12 text-center">
                    <p className="text-gray-500 text-lg">No embeddings found for this UPC</p>
                  </div>
                )}
                {/* Grid of images */}
                {embeddings.length > 0 && (
                  <div className="space-y-8">
                    {/* Pending Embeddings Section */}
                    {pendingEmbeddings.length > 0 && (
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <h2 className="text-xl font-bold text-gray-900">
                            Pending Embeddings ({pendingEmbeddings.length})
                          </h2>
                          <EmbeddingTypeFilter value={filterType} onChange={handleFilterChange} />
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-4">
                          {pendingEmbeddings.map((embedding) => {
                            const isRejected = rejectedIds.has(
                              embedding.productEmbeddingCropId || ''
                            );
                            const isSelected = selectedIds.has(
                              embedding.productEmbeddingCropId || ''
                            );

                            const canReject = selectingMode !== EmbeddingSelectionMode.ACTIVATING;

                            return (
                              <EmbeddingTile
                                key={embedding.productEmbeddingCropId}
                                canReject={canReject}
                                selectingMode={selectingMode}
                                isSelected={isSelected || isRejected}
                                onReject={handleReject}
                                isDisabled={false}
                                embedding={embedding}
                                onCheckboxChange={handleCheckboxChange}
                                onUseAsProductImage={handleUseAsProductImage}
                              ></EmbeddingTile>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Active Embeddings Section */}
                    {activeEmbeddings.length > 0 && (
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <h2 className="text-xl font-bold text-gray-900">
                            Active Embeddings ({activeEmbeddings.length})
                          </h2>
                          {pendingEmbeddings.length === 0 && (
                            <EmbeddingTypeFilter value={filterType} onChange={handleFilterChange} />
                          )}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-6">
                          {activeEmbeddings.map((embedding) => {
                            const isSelected = selectedIds.has(
                              embedding.productEmbeddingCropId || ''
                            );
                            const isDisabled =
                              selectingMode === EmbeddingSelectionMode.ACTIVATING ||
                              (rejectedIds.size > 0 && !embedding.isActive);

                            return (
                              <EmbeddingTile
                                key={embedding.productEmbeddingCropId}
                                canReject={false}
                                selectingMode={selectingMode}
                                isSelected={isSelected}
                                isDisabled={isDisabled}
                                embedding={embedding}
                                onCheckboxChange={handleCheckboxChange}
                                onUseAsProductImage={handleUseAsProductImage}
                              ></EmbeddingTile>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Inactive Embeddings Section */}
                    {inactiveEmbeddings.length > 0 && (
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <h2 className="text-xl font-bold text-gray-900">
                            Inactive Embeddings ({inactiveEmbeddings.length})
                          </h2>
                          {pendingEmbeddings.length === 0 && activeEmbeddings.length === 0 && (
                            <EmbeddingTypeFilter value={filterType} onChange={handleFilterChange} />
                          )}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-4">
                          {inactiveEmbeddings.map((embedding) => {
                            const isSelected = selectedIds.has(
                              embedding.productEmbeddingCropId || ''
                            );
                            const isDisabled =
                              selectingMode === EmbeddingSelectionMode.DEACTIVATING ||
                              (rejectedIds.size > 0 && !embedding.isActive);

                            return (
                              <EmbeddingTile
                                key={embedding.productEmbeddingCropId}
                                canReject={false}
                                selectingMode={selectingMode}
                                isSelected={isSelected}
                                isDisabled={isDisabled}
                                embedding={embedding}
                                onCheckboxChange={handleCheckboxChange}
                                onUseAsProductImage={handleUseAsProductImage}
                              ></EmbeddingTile>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* No results after filtering */}
                    {pendingEmbeddings.length === 0 &&
                      activeEmbeddings.length === 0 &&
                      inactiveEmbeddings.length === 0 && (
                        <div>
                          <div className="flex items-center justify-between mb-4">
                            <div />
                            <EmbeddingTypeFilter value={filterType} onChange={handleFilterChange} />
                          </div>
                          <p className="text-gray-500 text-center py-8">
                            No embeddings match the selected filter
                          </p>
                        </div>
                      )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Fixed bottom action bar */}
      {(selectedIds.size > 0 || rejectedIds.size > 0) && (
        <div className="fixed bottom-0 left-0 right-0 z-20 bg-white border-t border-gray-200 shadow-lg px-6 py-3">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <button
              onClick={() => {
                setSelectedIds(new Set());
                setRejectedIds(new Set());
                setSelectingMode(null);
              }}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Clear Selection
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className={`px-6 py-3 text-white rounded-md disabled:bg-gray-400 disabled:cursor-not-allowed font-medium ${
                selectingMode === EmbeddingSelectionMode.DEACTIVATING
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-green-600 hover:bg-green-700'
              }`}
            >
              {saving
                ? 'Saving...'
                : `Set ${selectedIds.size + rejectedIds.size} as ${
                    selectingMode === EmbeddingSelectionMode.DEACTIVATING ? 'Inactive' : 'Active'
                  }`}
            </button>
          </div>
        </div>
      )}

      {/* Product Images Management Modal */}
      <ProductImagesModal
        isOpen={showImagesModal}
        onClose={() => {
          setShowImagesModal(false);
          setImagesModalPendingNew(undefined);
        }}
        primaryImage={
          productImages.primaryImage?.productImageId ? productImages.primaryImage : null
        }
        alternativeImages={productImages.alternativeImages}
        shelfImages={productImages.images.filter((img) => !img.productImageId)}
        otherChainImages={productImages.allImages.filter(
          (img) => img.chainId !== Number(activeChainId)
        )}
        currentChainId={activeChainId ? Number(activeChainId) : 0}
        chainNamesMap={chainNamesMap}
        embeddings={embeddings}
        pendingNewImage={imagesModalPendingNew}
        onSave={handleProductImagesSave}
      />

      {/* Image Zoom Modal */}
      <ImageZoomModal
        isOpen={!!zoomImageSrc}
        imageSrc={zoomImageSrc || ''}
        onClose={() => setZoomImageSrc(null)}
      />

      {/* Use as Product Image Dialog */}
      <UseAsProductImageDialog
        isOpen={!!useAsProductImageEmbedding}
        imageUrl={useAsProductImageEmbedding?.vizUrl || ''}
        onClose={() => setUseAsProductImageEmbedding(null)}
        onConfirm={handleUseAsProductImageConfirm}
      />

      {/* Pending Embeddings Confirmation Dialog */}
      {selectingMode && (
        <PendingEmbeddingsDialog
          open={showPendingDialog}
          selectedCount={selectedIds.size + rejectedIds.size}
          remainingCount={getRemainingPendingIds().length}
          selectingMode={selectingMode}
          onIncludeRemaining={() => {
            setShowPendingDialog(false);
            executeSave(true);
          }}
          onSkipRemaining={() => {
            setShowPendingDialog(false);
            executeSave(false);
          }}
        />
      )}
    </div>
  );
};

export default EmbeddingDetailPage;
